import UpdateForm from "@/components/extension/profiles/UpdateForm";
import * as React from "react";
import {Autocomplete, Checkbox, InputAdornment, Theme, TextField} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {defineMessage, FormattedMessage, MessageDescriptor, useIntl} from "react-intl";
import {formCheckboxMargin} from "@/constants";
import MultipleLineError from "@/components/MultipleLineError";
import {UpdateUserProfileData} from "@/api";
import {useRestrictedDisabled} from "@/components/extension/profiles/tools";

const translations = {
    options: {
        voicemail: defineMessage({id: "callForward.options.voicemail", defaultMessage: "Voicemail"}),
        callpark: defineMessage({id: "callForward.options.callpark", defaultMessage: "Call park"}),
        number: defineMessage({id: "callForward.options.number", defaultMessage: "{number}"}),
    },
    notes: {
        home: defineMessage({id: "callForward.notes.home", defaultMessage: "Home"}),
        mobile: defineMessage({id: "callForward.notes.mobile", defaultMessage: "Mobile"}),
    },
} as const;

type Field = keyof Pick<
    UpdateUserProfileData,
    | "callForwardUnconditionalNumber"
    | "parallelRingingNumber"
    | "callForwardUnavailableNumber"
    | "callForwardNoanswerNumber"
    | "callForwardFaultNumber"
    | "callForwardBusyNumber"
>;

type NumberField = keyof Pick<
    UpdateUserProfileData,
    | "callForwardUnconditional"
    | "parallelRinging"
    | "callForwardUnavailable"
    | "callForwardNoanswer"
    | "callForwardFault"
    | "callForwardBusy"
>;

type CallForwardSelectProps = {
    field: Field;
    form: UpdateForm;
    voicemail?: boolean;
    callPark?: boolean;
    disabled: boolean;
    profileId: number;
    extra?: React.ReactNode;
};

type Option = {
    value: string;
    label: MessageDescriptor;
    labelValues?: Record<string, string>;
    note?: MessageDescriptor;
};

enum SpecialNumbers {
    voicemail = "voicemail",
    callPark = "callpark",
}

function createOptions(form: UpdateForm, field: Field, profileId: number, voicemail: boolean, callPark: boolean) {
    const options: Option[] = [];
    const data = form.dataSource.data.current;
    const homeNumber = data.extension.homeNumber;
    const mobileNumber = data.extension.mobileNumber;
    const profile = form.dataSource.getProfile(profileId);
    const savedNumber: string = profile[field.replace("Number", "SavedNumber") as Field];
    const specNumbers = [SpecialNumbers.voicemail, SpecialNumbers.callPark, homeNumber, mobileNumber];

    if (voicemail)
        options.push({
            value: SpecialNumbers.voicemail,
            label: translations.options.voicemail,
        });

    if (callPark)
        options.push({
            value: SpecialNumbers.callPark,
            label: translations.options.callpark,
        });

    if (homeNumber?.length)
        options.push({
            value: homeNumber,
            label: translations.options.number,
            labelValues: {number: homeNumber},
            note: translations.notes.home,
        });

    if (mobileNumber?.length)
        options.push({
            value: mobileNumber,
            label: translations.options.number,
            labelValues: {number: mobileNumber},
            note: translations.notes.mobile,
        });

    if (form.values.profile[field]?.length && !specNumbers.includes(form.values.profile[field]))
        options.push({
            value: form.values.profile[field],
            label: translations.options.number,
            labelValues: {number: form.values.profile[field]},
        });

    if (savedNumber?.length && form.values.profile[field] !== savedNumber)
        options.push({
            value: savedNumber,
            label: translations.options.number,
            labelValues: {number: savedNumber},
        });

    return options;
}

function getNote(form: UpdateForm, field: Field): MessageDescriptor {
    const data = form.dataSource.data.current;
    const homeNumber = data.extension.homeNumber;
    const mobileNumber = data.extension.mobileNumber;
    const value = form.values.profile[field];

    if (!value || value === "") return null;

    switch (value) {
        case homeNumber:
            return translations.notes.home;
        case mobileNumber:
            return translations.notes.mobile;
        default:
            return null;
    }
}

const useNoteStyles = makeStyles()((theme: Theme) => ({
    noteContainer: {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius,
        padding: "0px 4px",
        opacity: 0.7,
        fontSize: "0.8em",
    },
}));

const Note = ({label, style}: {label: MessageDescriptor; style?: React.CSSProperties}) => {
    const {classes} = useNoteStyles();
    return (
        <span className={classes.noteContainer} style={style}>
            <FormattedMessage {...label} />
        </span>
    );
};

export const CallForwardSelect = ({
    callPark,
    disabled,
    field,
    form,
    voicemail,
    profileId,
    extra,
}: CallForwardSelectProps) => {
    const enabledFieldName = field.replace("Number", "") as NumberField;
    const options = createOptions(form, field, profileId, voicemail, callPark);
    const intl = useIntl();
    const errors = form.getProfileErrors(field).concat(...form.getProfileErrors(enabledFieldName));
    const note: MessageDescriptor = getNote(form, field);

    const rawCurrent = form.values.profile[field] ?? "";
    const selectedOption = options.find((o) => o.value === rawCurrent);
    const displayInputValue = selectedOption
        ? intl.formatMessage(selectedOption.label, selectedOption.labelValues)
        : rawCurrent;

    return (
        <div style={{display: "flex", alignItems: "flex-start"}}>
            <Checkbox
                color="primary"
                disabled={useRestrictedDisabled(enabledFieldName, disabled)}
                checked={form.values.profile[enabledFieldName]}
                onClick={() => form.updateProfileValues({[enabledFieldName]: !form.values.profile[enabledFieldName]})}
                style={{marginLeft: formCheckboxMargin}}
            />
            <Autocomplete<Option, false, false, true>
                // Ensure input is wide enough for ~20 characters (fallback to layout if larger)
                sx={{minWidth: "20ch"}}
                freeSolo
                disabled={useRestrictedDisabled(field, disabled)}
                options={options}
                value={
                    (function () {
                        const current = form.values.profile[field] ?? "";
                        const found = options.find((o) => o.value === current);
                        return found ?? (current ? (current as unknown as Option | string) : null);
                    })() as any
                }
                // Keep the raw string the user is typing separate so freeSolo text isn't replaced by object label mid-typing
                inputValue={displayInputValue}
                onInputChange={(_, newInput, reason) => {
                    if (reason === "input") form.updateProfileValues({[field]: newInput});
                }}
                onChange={(_, newValue) => {
                    const val = typeof newValue === "string" ? newValue : (newValue?.value ?? "");
                    form.updateProfileValues({[field]: val});
                }}
                isOptionEqualToValue={(option, value) => {
                    if (!value) return false;
                    if (typeof value === "string") return option.value === value;
                    return option.value === value.value; // value narrowed to Option in this branch
                }}
                getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option.value ? intl.formatMessage(option.label, option.labelValues) : "";
                }}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={option.value} style={{display: "flex", alignItems: "center"}}>
                            {intl.formatMessage(option.label, option.labelValues)}
                            {option.note && (
                                <span style={{marginLeft: 8}}>
                                    <Note label={option.note} />
                                </span>
                            )}
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        style={{margin: 0}}
                        error={errors.length > 0}
                        helperText={errors.length ? <MultipleLineError errors={errors} /> : null}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {note && (
                                        <InputAdornment position="end">
                                            <Note label={note} />
                                        </InputAdornment>
                                    )}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            {extra}
        </div>
    );
};
